import { useEffect, useRef, useCallback, useState, createContext } from "react";
import { ControllerType, ControllerEvents } from "components/util/controller";
import { useAppControllerActions } from "components/presentation/main/Main.lib.actions";

// Bridge exposed to the project's app, which renders in this realm: it registers one
// event handler and sends controller actions back through it. Provided by Main so the
// mount can consume it via context without prop threading.
//
// Routing is not part of it: the app routes on this page's own history (handed to it by
// Main.sub.App.view), so where it is is already on the URL and nothing has to be mirrored.
export const HostControllerContext = createContext(null);

export const useAppViewController = ({ flow }) => {

  // The app registers (handler) on mount and unregisters (null) on unmount. Everything the
  // host pushes to the project goes through it.
  const appHandlerRef = useRef(null);

  // Chat slots declared by the app, consumed by Main.sub.ChatPortal to render <Chat />
  // instances as React portals into the placeholders the app rendered.
  const [chats, setChats] = useState({});

  // The side menu the app declared, consumed by Main.sub.MenuPortal to render it as a
  // React portal into the placeholder the app rendered. Keyed by slot like the chats: an
  // app normally mounts one, but nothing stops it from mounting two.
  const [menus, setMenus] = useState({});

  // Whether each menu's solution panel is open. Kept here rather than inside AppMenu
  // because the app can raise it too (/helpers/Menu's openMenu), and a flag the menu owned
  // privately would be unreachable from the other side of the bridge.
  const [openMenus, setOpenMenus] = useState({});

  // The screenshot a view asked for, or null. Only one runs at a time app-wide: the user
  // can only point at one thing. The ref mirrors the state so the reply can be routed
  // without waiting for a render.
  const [screenshotRequest, setScreenshotRequest] = useState(null);
  const screenshotRequestRef = useRef(null);

  const registerApp = useCallback((handler) => {
    appHandlerRef.current = handler || null;
  }, []);

  const dispatchToApp = useCallback((message) => {
    appHandlerRef.current?.(message);
  }, []);

  const registerChat = useCallback((slotId, chatProps) => {
    if (!slotId) return;
    setChats(prev => ({ ...prev, [slotId]: chatProps || {} }));
  }, []);

  const unregisterChat = useCallback((slotId) => {
    if (!slotId) return;
    setChats(prev => {
      if (!(slotId in prev)) return prev;
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  }, []);

  const updateChatProps = useCallback((slotId, chatProps) => {
    if (!slotId) return;
    setChats(prev => {
      if (!(slotId in prev)) return prev;
      return { ...prev, [slotId]: { ...(prev[slotId] || {}), ...(chatProps || {}) } };
    });
  }, []);

  const registerMenu = useCallback((slotId, menuProps) => {
    if (!slotId) return;
    setMenus(prev => ({ ...prev, [slotId]: menuProps || {} }));
  }, []);

  const unregisterMenu = useCallback((slotId) => {
    if (!slotId) return;
    setMenus(prev => {
      if (!(slotId in prev)) return prev;
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  }, []);

  const updateMenuProps = useCallback((slotId, menuProps) => {
    if (!slotId) return;
    setMenus(prev => {
      if (!(slotId in prev)) return prev;
      return { ...prev, [slotId]: { ...(prev[slotId] || {}), ...(menuProps || {}) } };
    });
  }, []);

  // `isOpen: null` means toggle, so a trigger in the app does not have to track what the
  // panel is currently doing.
  const setMenuOpen = useCallback((slotId, isOpen) => {
    if (!slotId) return;
    setOpenMenus(prev => ({
      ...prev,
      [slotId]: isOpen == null ? !prev[slotId] : Boolean(isOpen),
    }));
  }, []);

  // Imperative handles of the chats currently portaled into the app, by slot. An action
  // the app sends — a hidden message, the answer to a component — has to reach one
  // conversation in particular, and only the mounted Chat can send on its own chat id.
  const chatApisRef = useRef({});

  const registerChatApi = useCallback((slotId, api) => {
    if (!slotId) return;
    if (api) chatApisRef.current[slotId] = api;
    else delete chatApisRef.current[slotId];
  }, []);

  // A view with a single chat does not have to name it. With several, the slot is the
  // only way to tell them apart, so an unnamed send has nowhere unambiguous to go.
  const resolveChatApi = useCallback((slotId) => {
    const apis = chatApisRef.current;
    if (slotId) return apis[slotId] || null;
    const slotIds = Object.keys(apis);
    return slotIds.length == 1 ? apis[slotIds[0]] : null;
  }, []);

  // Components the agent asked for and is still waiting on. The app answers with the
  // request id alone; everything needed to resume the execution is kept here, so no
  // node or execution id ever crosses into project code.
  const componentRequestsRef = useRef(new Map());

  const registerComponentRequest = useCallback((request) => {
    if (!request?.requestId) return;
    componentRequestsRef.current.set(request.requestId, request);
  }, []);

  const takeComponentRequest = useCallback((requestId) => {
    const request = componentRequestsRef.current.get(requestId);
    if (request) componentRequestsRef.current.delete(requestId);
    return request || null;
  }, []);

  const takeScreenshot = useCallback(({ requestId, options }) => {
    if (!requestId) return;
    const request = { requestId, options: options || {} };
    screenshotRequestRef.current = request;
    setScreenshotRequest(request);
  }, []);

  // Answers the pending request exactly once. `result` is { screenshot, annotations }, or
  // null when the user aborted — the app resolves its promise with whichever arrives.
  const finishScreenshot = useCallback((result) => {
    const request = screenshotRequestRef.current;
    if (!request) return;
    screenshotRequestRef.current = null;
    setScreenshotRequest(null);
    dispatchToApp({
      type: ControllerType.EVENT,
      name: ControllerEvents.OnScreenshotTaken,
      requestId: request.requestId,
      result: result || null,
    });
  }, [dispatchToApp]);

  const appStorageRef = useRef({});
  const getAppStorage = useCallback(() => appStorageRef.current, []);
  const setAppStorage = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    appStorageRef.current = { ...appStorageRef.current, ...patch };
    dispatchToApp({
      type: ControllerType.EVENT,
      name: ControllerEvents.OnAppStorageChanged,
      storage: appStorageRef.current,
    });
  }, [dispatchToApp]);

  const { handlers: actionHandlers, resolveChatProps } = useAppControllerActions({
    flow,
    dispatchToApp,
    registerChat,
    unregisterChat,
    updateChatProps,
    registerMenu,
    unregisterMenu,
    updateMenuProps,
    setMenuOpen,
    resolveChatApi,
    registerComponentRequest,
    takeComponentRequest,
    takeScreenshot,
    getAppStorage,
    setAppStorage,
  });

  // Chats are stored with the raw chatProps the app registered (which may include
  // processName). Resolve to a node id here so the portal and Chat only see what they
  // need. Recomputed on every render so chats registered before rootNodes loaded get
  // their nodeId backfilled once the lookup resolves.
  const resolvedChats = {};
  for (const [slotId, chatProps] of Object.entries(chats))
    resolvedChats[slotId] = resolveChatProps(chatProps);

  const handleControllerMessage = (message) => {
    if (message.type === ControllerType.ACTION) {
      const actionHandler = actionHandlers[message.name];
      if (actionHandler) actionHandler(message);
      return;
    }

    dispatchToApp(message);
  }

  const handleControllerMessageRef = useRef(handleControllerMessage);

  useEffect(() => {
    handleControllerMessageRef.current = handleControllerMessage;
  });

  // What the server pushes (execution events, a process's reply or message) reaches the app
  // over its own socket; only what the app and the host's own chrome send passes through here.
  const sendControllerAction = useCallback((action) => {
    handleControllerMessageRef.current(action);
  }, []);

  return {
    registerApp,
    registerChatApi,
    sendControllerAction,
    chats: resolvedChats,
    menus,
    openMenus,
    setMenuOpen,
    screenshotRequest,
    finishScreenshot,
  };

}
